// Three.js bubble chart — integrated labels, OrbitControls, proper clipping
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

// ── Canvas texture factories ──

function makeBubbleLabelTex(rank, shortName, barrierColor) {
  const w = 256, h = 128;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');

  // Solid dark background — opaque, no transparency
  ctx.fillStyle = 'rgba(6,8,22,0.95)';
  ctx.fillRect(0, 0, w, h);

  // Rank circle
  const cx = 44, cy = 64;
  ctx.beginPath(); ctx.arc(cx, cy, 18, 0, Math.PI * 2);
  ctx.fillStyle = barrierColor;
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 1.5; ctx.stroke();

  ctx.font = 'bold 15px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(rank), cx, cy);

  // Name
  ctx.font = 'bold 16px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#f0f0f8';
  ctx.textAlign = 'left';
  ctx.fillText(shortName, 76, 58);

  // Barrier tag
  ctx.font = '10px "JetBrains Mono", monospace';
  ctx.fillStyle = barrierColor;
  ctx.textAlign = 'left';
  const bl = barrierColor === '#C0392B' ? 'HIGH BARRIER' : barrierColor === '#E67E22' ? 'MED BARRIER' : 'LOW BARRIER';
  ctx.fillText(bl, 76, 80);

  const tex = new THREE.CanvasTexture(c);
  tex.premultiplyAlpha = true;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function makeExpanderTex(rank, shortName, detail, barrierLabel, value, barrierColor) {
  const w = 440, h = 210;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');

  // Fully opaque dark background
  ctx.fillStyle = 'rgba(8,10,26,0.96)';
  ctx.fillRect(0, 0, w, h);

  // Border
  ctx.strokeStyle = barrierColor;
  ctx.lineWidth = 2;
  ctx.strokeRect(2, 2, w - 4, h - 4);

  // Title
  ctx.font = 'bold 16px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#f0f0f8';
  ctx.textAlign = 'left';
  ctx.fillText(`#${rank}  ${shortName}`, 22, 38);

  // Stats
  ctx.font = '12px "JetBrains Mono", monospace';
  ctx.fillStyle = barrierColor;
  ctx.fillText(`BARRIER: ${barrierLabel}`, 22, 60);
  ctx.fillStyle = '#b0b8d0';
  ctx.fillText(`VALUE: ${value}/10`, 22, 78);

  // Divider
  ctx.strokeStyle = 'rgba(100,110,150,0.35)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(22, 90); ctx.lineTo(w - 22, 90); ctx.stroke();

  // Detail
  ctx.font = '11px Inter, system-ui, sans-serif';
  ctx.fillStyle = 'rgba(210,218,240,0.85)';
  const words = detail.split(' ');
  let line = '', y = 108;
  for (const word of words) {
    const test = line + word + ' ';
    if (ctx.measureText(test).width > w - 44) {
      ctx.fillText(line.trim(), 22, y);
      line = word + ' ';
      y += 18;
    } else { line = test; }
  }
  ctx.fillText(line.trim(), 22, y);

  const tex = new THREE.CanvasTexture(c);
  tex.premultiplyAlpha = true;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export async function initOpportunityChart(containerId, data) {
  const container = document.getElementById(containerId);
  const canvas = document.getElementById('opp-chart');
  if (!canvas || !container) return;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x060614);
  scene.fog = new THREE.FogExp2(0x060614, 0.012);

  const camera = new THREE.PerspectiveCamera(50, container.clientWidth / container.clientHeight, 0.1, 60);
  camera.position.set(6, 5.5, 9.5);

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.setClearColor(0x060614, 1);

  // ── OrbitControls ──
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.06;
  controls.enablePan = true;
  controls.enableZoom = true;
  controls.minDistance = 3;
  controls.maxDistance = 20;
  controls.target.set(5.5, 6.5, 0);
  controls.autoRotate = true;
  controls.autoRotateSpeed = 0.6;
  controls.update();

  // Lighting
  scene.add(new THREE.AmbientLight(0x3a3a5c, 1.0));
  const dl = new THREE.DirectionalLight(0x8899ff, 2.2);
  dl.position.set(10, 15, 8);
  scene.add(dl);
  const dl2 = new THREE.DirectionalLight(0x4466aa, 0.7);
  dl2.position.set(-5, 5, -5);
  scene.add(dl2);

  // Grid floor
  const grid = new THREE.Mesh(
    new THREE.PlaneGeometry(12, 12, 20, 20),
    new THREE.MeshBasicMaterial({ color: 0x1a1a3a, wireframe: true, transparent: true, opacity: 0.28, depthWrite: false })
  );
  grid.rotation.x = -Math.PI / 2;
  grid.position.set(5.5, 0.1, 6.5);
  scene.add(grid);

  // Quadrant separator lines
  const qLine = new THREE.LineBasicMaterial({ color: 0x334466, transparent: true, opacity: 0.4, depthWrite: false });
  scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(
    [new THREE.Vector3(5.5, 0.15, 0.05), new THREE.Vector3(5.5, 11, 0.05)]), qLine));
  scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(
    [new THREE.Vector3(0, 7, 0.05), new THREE.Vector3(11, 7, 0.05)]), qLine));

  // ── Bubbles ──
  const sphereGeo = new THREE.SphereGeometry(1, 40, 40);
  const wireGeo = new THREE.SphereGeometry(1.01, 20, 20);
  const bubbles = new THREE.Group();
  scene.add(bubbles);
  const bubbleObjs = [];

  for (const opp of data) {
    const barrierLabel = opp.barrier >= 7 ? 'HIGH' : opp.barrier >= 5 ? 'MED' : 'LOW';
    const barrierColor = opp.barrier >= 7 ? '#C0392B' : opp.barrier >= 5 ? '#E67E22' : '#27AE60';
    const s = Math.sqrt(opp.size) * 0.042;
    const x = opp.barrier, y = opp.value;
    const t = (opp.barrier - 2) / 8;
    const color = new THREE.Color().setHSL(0.55 + t * 0.30, 0.65, 0.42 + (1 - t) * 0.22);
    const radius = s;

    // Bubble
    const mat = new THREE.MeshStandardMaterial({
      color, roughness: 0.35, metalness: 0.1,
      transparent: true, opacity: 0.78,
    });
    const mesh = new THREE.Mesh(sphereGeo, mat);
    mesh.position.set(x, y, 0);
    mesh.scale.setScalar(radius);
    mesh.userData = { opp, baseY: y, baseScale: radius, hoverScale: radius };
    bubbles.add(mesh);

    // Wireframe shell
    const wire = new THREE.Mesh(wireGeo, new THREE.MeshBasicMaterial({
      color: 0x4455aa, wireframe: true, transparent: true, opacity: 0.10, depthWrite: false,
    }));
    mesh.add(wire);

    // Label plane — offset from center, on bubble surface
    const labelTex = makeBubbleLabelTex(opp.rank, opp.shortName || opp.name, barrierColor);
    const labelPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(radius * 1.6, radius * 0.8),
      new THREE.MeshStandardMaterial({
        map: labelTex,
        roughness: 0.5, metalness: 0.1,
        transparent: true, depthWrite: false, side: THREE.DoubleSide,
      })
    );
    const labelOffset = new THREE.Vector3(0, radius * 0.35, radius * 1.08);
    labelPlane.position.copy(labelOffset);
    mesh.add(labelPlane);

    // Expander plane
    const expanderTex = makeExpanderTex(opp.rank, opp.shortName || opp.name, opp.detail, barrierLabel, opp.value, barrierColor);
    const expanderPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(radius * 3.0, radius * 1.45),
      new THREE.MeshStandardMaterial({
        map: expanderTex,
        roughness: 0.4, metalness: 0.1,
        transparent: true, depthWrite: false, depthTest: true, side: THREE.DoubleSide,
      })
    );
    expanderPlane.visible = false;
    expanderPlane.position.set(0, radius * 1.4, radius * 1.15);
    mesh.add(expanderPlane);

    bubbleObjs.push({ mesh, label: labelPlane, expander: expanderPlane, opp, radius });
  }

  // ── Raycast + interaction ──
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2(-10, -10);
  let hoveredRank = null;
  let pinnedRank = null;

  canvas.addEventListener('pointermove', (e) => {
    const rect = canvas.getBoundingClientRect();
    mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  });

  canvas.addEventListener('click', () => {
    if (hoveredRank !== null) {
      pinnedRank = pinnedRank === hoveredRank ? null : hoveredRank;
      controls.autoRotate = false;
    }
  });

  // Reset auto-rotate when user stops dragging
  canvas.addEventListener('pointerdown', () => { controls.autoRotate = false; });

  // ── Animation ──
  let animId;
  const clock = new THREE.Clock();

  function animate() {
    animId = requestAnimationFrame(animate);
    const dt = Math.min(clock.getDelta(), 0.1);

    controls.update();

    // Raycast
    raycaster.setFromCamera(mouse, camera);
    let newHovered = null;
    const hitTargets = [];
    for (const bo of bubbleObjs) {
      hitTargets.push(bo.mesh);
      if (bo.expander.visible) hitTargets.push(bo.expander);
    }
    const hits = raycaster.intersectObjects(hitTargets, false);
    if (hits.length) {
      for (const bo of bubbleObjs) {
        if (hits[0].object === bo.mesh || hits[0].object === bo.expander) {
          newHovered = bo.opp.rank;
          break;
        }
      }
    }
    hoveredRank = newHovered;
    canvas.style.cursor = hoveredRank !== null ? 'pointer' : 'grab';

    // Float + expand
    for (const bo of bubbleObjs) {
      const phase = bo.opp.rank * 0.7;
      bo.mesh.position.y = bo.opp.value + Math.sin(Date.now() * 0.001 + phase) * 0.13;

      const shouldExpand = bo.opp.rank === hoveredRank || bo.opp.rank === pinnedRank;
      bo.expander.visible = shouldExpand;
      bo.label.visible = !shouldExpand;

      const targetScale = shouldExpand ? bo.radius * 1.12 : bo.radius;
      bo.mesh.userData.hoverScale += (targetScale - bo.mesh.userData.hoverScale) * 8 * dt;
      bo.mesh.scale.setScalar(bo.mesh.userData.hoverScale);
    }

    renderer.render(scene, camera);
  }

  const ro = new ResizeObserver(() => {
    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.clientWidth, container.clientHeight);
  });
  ro.observe(container);

  animate();

  return {
    destroy() {
      cancelAnimationFrame(animId);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      scene.traverse(obj => { if (obj.geometry) obj.geometry.dispose(); if (obj.material) obj.material.dispose(); });
    },
  };
}
